import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Send, Clock, Users, DollarSign, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useSettings } from '../context/SettingsContext';
import { useLanguage } from '../context/LanguageContext';
import SearchableLocation from './SearchableLocation';

const ExcursionBookingDialog = ({ excursion, open, onOpenChange }) => {
  const { t, lang } = useLanguage();
  const { settings } = useSettings();
  const [airports, setAirports] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', whatsapp: '',
    pickup: '', date: '', time: '',
    passengers: '2', comments: '',
  });

  useEffect(() => {
    if (open) {
      api.get('/airports').then(r => setAirports(r.data)).catch(()=>{});
      api.get('/hotels').then(r => setHotels(r.data)).catch(()=>{});
    }
  }, [open]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (!excursion) return null;

  const title = lang === 'en' && excursion.title_en ? excursion.title_en : excursion.title;
  const duration = lang === 'en' && excursion.duration_en ? excursion.duration_en : excursion.duration;
  const desc = lang === 'en' && excursion.description_en ? excursion.description_en : excursion.description;

  const total = (parseFloat(excursion.price) || 0) * parseInt(form.passengers || '1', 10);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.whatsapp || !form.date) {
      toast.error(lang === 'es' ? 'Completa los campos requeridos' : 'Please complete required fields');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        type: 'excursion',
        full_name: form.full_name,
        email: form.email,
        whatsapp: form.whatsapp,
        trip_type: 'one-way',
        pickup: form.pickup,
        destination: title,
        excursion_id: excursion.id,
        excursion_title: title,
        date: form.date,
        time: form.time,
        passengers: parseInt(form.passengers || '2', 10),
        vehicle: '',
        comments: `${form.comments} | Total estimado: $${total.toFixed(0)} USD (${form.passengers} pax × $${excursion.price})`,
      };
      await api.post('/reservations', payload);
      toast.success(lang === 'es' ? '¡Reserva enviada! Te contactaremos por WhatsApp.' : 'Booking sent! We will contact you via WhatsApp.');
      const num = (settings && settings.whatsapp_number) || '18099638198';
      const msg = lang === 'es'
        ? `Hola TTS Santos, quiero reservar la excursión: *${title}*. Nombre: ${form.full_name}, Pickup: ${form.pickup || '(a definir)'}, Fecha: ${form.date} ${form.time || ''}, Pasajeros: ${form.passengers}, Total estimado: $${total.toFixed(0)} USD.`
        : `Hello TTS Santos, I want to book the excursion: *${title}*. Name: ${form.full_name}, Pickup: ${form.pickup || '(TBD)'}, Date: ${form.date} ${form.time || ''}, Passengers: ${form.passengers}, Estimated total: $${total.toFixed(0)} USD.`;
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
      onOpenChange(false);
      setForm({ full_name: '', email: '', whatsapp: '', pickup: '', date: '', time: '', passengers: '2', comments: '' });
    } catch {
      toast.error(lang === 'es' ? 'Error al enviar' : 'Error sending');
    } finally {
      setLoading(false);
    }
  };

  const paxOptions = [1,2,3,4,5,6,7,8,9,10,11,12,15,20,25,30];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-white/10 text-white max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {lang === 'es' ? 'Reservar' : 'Book'} · {title}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 my-3 p-3 bg-black/40 rounded-lg border border-white/5">
          <div className="text-center">
            <DollarSign className="w-4 h-4 text-[#ff6b1a] mx-auto" />
            <div className="text-lg font-black text-white mt-1">${excursion.price.toFixed(0)}</div>
            <div className="text-[10px] text-white/50">{lang === 'es' ? 'por persona' : 'per person'}</div>
          </div>
          <div className="text-center border-x border-white/10">
            <Clock className="w-4 h-4 text-[#ff6b1a] mx-auto" />
            <div className="text-sm font-bold text-white mt-1">{duration}</div>
            <div className="text-[10px] text-white/50">{lang === 'es' ? 'duración' : 'duration'}</div>
          </div>
          <div className="text-center">
            <Users className="w-4 h-4 text-[#ff6b1a] mx-auto" />
            <div className="text-sm font-bold text-white mt-1">{form.passengers} pax</div>
            <div className="text-[10px] text-[#ff6b1a] font-bold">${total.toFixed(0)} {lang === 'es' ? 'total' : 'total'}</div>
          </div>
        </div>

        <p className="text-white/60 text-xs mb-3">{desc}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Nombre Completo' : 'Full Name'} *</Label>
              <Input value={form.full_name} onChange={(e) => update('full_name', e.target.value)} required className="bg-black/40 border-white/10 text-white h-10" />
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required className="bg-black/40 border-white/10 text-white h-10" />
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs">WhatsApp *</Label>
              <Input value={form.whatsapp} onChange={(e) => update('whatsapp', e.target.value)} required placeholder="+1 829..." className="bg-black/40 border-white/10 text-white h-10" />
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Pasajeros' : 'Passengers'} *</Label>
              <Select value={form.passengers} onValueChange={(v) => update('passengers', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-white/10">
                  {paxOptions.map(p => <SelectItem key={p} value={String(p)} className="text-white">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> {lang === 'es' ? 'Fecha' : 'Date'} *</Label>
              <Input type="date" value={form.date} onChange={(e) => update('date', e.target.value)} required className="bg-black/40 border-white/10 text-white h-10" />
            </div>
            <div>
              <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Hora' : 'Time'}</Label>
              <Input type="time" value={form.time} onChange={(e) => update('time', e.target.value)} className="bg-black/40 border-white/10 text-white h-10" />
            </div>
          </div>

          <div>
            <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Recogida (Hotel)' : 'Pickup (Hotel)'}</Label>
            <SearchableLocation value={form.pickup} onChange={(v) => update('pickup', v)} airports={airports} hotels={hotels} placeholder={lang === 'es' ? 'Buscar hotel o aeropuerto...' : 'Search hotel or airport...'} />
          </div>

          <div>
            <Label className="text-white/80 mb-1.5 block text-xs">{lang === 'es' ? 'Comentarios (Opcional)' : 'Comments (Optional)'}</Label>
            <Textarea value={form.comments} onChange={(e) => update('comments', e.target.value)} rows={2} className="bg-black/40 border-white/10 text-white" />
          </div>

          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-white/15 text-white hover:bg-white/5 flex-1">
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button type="submit" disabled={loading} className="btn-orange border-0 flex-[2] gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> {lang === 'es' ? `Reservar — $${total.toFixed(0)}` : `Book — $${total.toFixed(0)}`}</>}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExcursionBookingDialog;
